import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";

import {
  ConfirmDialog,
  Dialog,
  DialogFooter,
  DialogHeader,
  Dropdown,
  ErrorState,
  FormField,
  StateMessage,
} from "@/ds";
import { CampoDecimal } from "@/shared/components";
import { NOME_LABEL, TIPO_LABEL, TIPO_PLACEHOLDER } from "./distribuicao-dialog.constants";
import styles from "./distribuicao-dialog.module.scss";
import type {
  BlocoColetores,
  BlocoFacilidades,
  DistribuicaoDialogViewModel,
} from "./types-distribuicao-dialog";

const RODAPE_DIALOGO = { footer: { className: styles.dialogoRodape } };
const noop = () => undefined;
const PAINEL = { panel: { className: styles.painel } };

function Bloco({ bloco, desabilitado }: { bloco: BlocoColetores; desabilitado: boolean }) {
  return (
    <section className={styles.bloco} data-testid={bloco.id}>
      <h3 className={styles.blocoTitulo}>{bloco.titulo}</h3>
      {bloco.erroSoma ? (
        <p className={styles.erroSoma} role="alert" data-testid={bloco.erroSomaTestId}>
          {bloco.erroSoma}
        </p>
      ) : null}
      <div className={styles.soma}>
        <p className={styles.dica}>{bloco.dica}</p>
        <Tag
          value={bloco.somaTexto}
          severity={bloco.somaValida ? "success" : "danger"}
          data-testid={bloco.somaTestId}
        />
      </div>
      <div className={styles.coletores}>
        {bloco.linhas.map((linha) => (
          <div key={linha.id} className={linha.sub ? styles.linhaSub : styles.linha}>
            <span className={styles.rotulo}>{linha.rotulo}</span>
            <div className={styles.campo}>
              <CampoDecimal
                label={linha.rotulo}
                labelOculto
                casas={2}
                value={linha.valor}
                onChange={linha.digitar}
                error={linha.erro}
                disabled={desabilitado || linha.desabilitado}
                testId={linha.testId}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Facilidades({ facilidades }: { facilidades: BlocoFacilidades }) {
  return (
    <section className={styles.bloco} data-testid="distribuicao-bloco-facilidades">
      <h3 className={styles.blocoTitulo}>{facilidades.titulo}</h3>
      <div className={styles.coletores}>
        {facilidades.linhas.map((linha) => (
          <div key={linha.id} className={styles.linha}>
            <span className={styles.rotulo}>{linha.rotulo}</span>
            <div className={styles.campo}>
              <CampoDecimal
                label={linha.rotulo}
                labelOculto
                casas={2}
                value={linha.valor}
                onChange={noop}
                disabled
                testId={`distribuicao-percentual-${linha.id}`}
              />
            </div>
          </div>
        ))}
      </div>
      <p className={styles.dica}>{facilidades.nota}</p>
    </section>
  );
}

export function DistribuicaoDialogView({
  formId,
  titulo,
  nome,
  tipo,
  uso,
  arvore,
  blocos,
  facilidades,
  isSubmitting,
  submitError,
  salvarDesabilitado,
  salvarAviso,
  enviar,
  cancelar,
  descarte,
  trocaTipo,
}: DistribuicaoDialogViewModel) {
  return (
    <>
      <Dialog
        visible
        size="xl"
        className={styles.dialogo}
        contentClassName={styles.corpo}
        onHide={cancelar}
        locked={isSubmitting}
        focusOnShow={false}
        header={<DialogHeader title={titulo} />}
        pt={RODAPE_DIALOGO}
        footer={
          <DialogFooter
            className={styles.rodape}
            confirmLabel="Salvar"
            confirmButtonProps={{
              disabled: salvarDesabilitado,
              tooltip: salvarAviso ?? undefined,
            }}
            formId={formId}
            loading={isSubmitting}
            locked={isSubmitting}
            onCancel={cancelar}
            testId="distribuicao"
          />
        }
        data-testid="distribuicao-dialog"
      >
        <form id={formId} className={styles.form} onSubmit={enviar} noValidate>
          {uso ? (
            <Message
              className="p-message-block"
              severity="info"
              text={uso}
              data-testid="distribuicao-aviso-uso"
            />
          ) : null}

          <div className={styles.identificacao}>
            <FormField.Root
              required
              error={nome.erro}
              help={nome.nota ?? undefined}
              disabled={nome.somenteLeitura}
            >
              <FormField.Label>{NOME_LABEL}</FormField.Label>
              <FormField.Control>
                <InputText
                  value={nome.valor}
                  maxLength={nome.max}
                  onChange={(evento) => nome.digitar(evento.target.value)}
                  disabled={nome.somenteLeitura || isSubmitting}
                  data-testid="distribuicao-nome"
                />
              </FormField.Control>
              <FormField.Help />
              <FormField.Error />
            </FormField.Root>
            <FormField.Root required error={tipo.erro} disabled={tipo.desabilitado}>
              <FormField.Label>{TIPO_LABEL}</FormField.Label>
              <FormField.Control>
                <Dropdown
                  appendTo="self"
                  pt={PAINEL}
                  value={tipo.valor}
                  options={tipo.opcoes}
                  placeholder={TIPO_PLACEHOLDER}
                  loading={tipo.carregando}
                  showClear={false}
                  onChange={(evento) => tipo.escolher(evento.value)}
                  data-testid="distribuicao-tipo"
                />
              </FormField.Control>
              <FormField.Error />
            </FormField.Root>
          </div>

          {arvore.estado === "carregando" ? (
            <StateMessage
              variant="loading"
              title={arvore.mensagem ?? ""}
              inline
              testId="distribuicao-carregando"
            />
          ) : null}
          {arvore.estado === "sem-tipo" ? (
            <p className={styles.dica} data-testid="distribuicao-sem-tipo">
              {arvore.mensagem}
            </p>
          ) : null}
          {arvore.estado === "erro" ? (
            <ErrorState
              message={arvore.mensagem ?? undefined}
              onRetry={arvore.recarregar}
              inline
              testId="distribuicao-erro-arvore"
            />
          ) : null}

          {blocos.length > 0 ? (
            <div className={styles.blocos}>
              {blocos.map((bloco) => (
                <Bloco key={bloco.id} bloco={bloco} desabilitado={isSubmitting} />
              ))}
            </div>
          ) : null}
          {facilidades ? <Facilidades facilidades={facilidades} /> : null}

          {submitError ? (
            <Message
              className="p-message-block"
              severity="error"
              text={submitError}
              data-testid="distribuicao-submit-error"
            />
          ) : null}
        </form>
      </Dialog>
      {descarte ? <ConfirmDialog {...descarte} /> : null}
      {trocaTipo ? <ConfirmDialog {...trocaTipo} /> : null}
    </>
  );
}
